// ===========================================
// DEMO EVENTS PAGE - EVENT SOURCING REPLAY
// ===========================================
// Replay recorded rounds from the round_events table
// Select table > round > seat > GO!
// ===========================================

import { useEffect, useRef, useState, useCallback } from 'react';
import type { BlackjackEvent } from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

interface TableOption {
  tableId: string;
  ownerSlug: string | null;
  firstEvent: string;
  lastEvent: string;
  roundCount: number;
}

interface RoundOption {
  handNumber: number;
  roundId: string;
  startedAt: string;
  endedAt: string;
  eventCount: number;
  isComplete: boolean;
}

interface SeatOption {
  seatPosition: number;
}

interface ReplayEvent extends BlackjackEvent {
  delay: number;
}

interface ReplaySummary {
  tableId: string;
  handNumber: number;
  dealer: { finalScore: number; outcome: string } | null;
  hands: Array<{
    seatPosition: number;
    handIndex: number;
    outcome: string;
    playerScore: number;
    dealerScore: number;
    betAmount: number;
    netResult: number;
  }>;
  totalNet: number;
}

interface CardDisplay {
  rank: string;
  suit: string;
  faceDown?: boolean;
}

// ===========================================
// API FUNCTIONS
// ===========================================

const API_BASE = '/api/demo-events';

async function fetchTables(): Promise<TableOption[]> {
  const res = await fetch(`${API_BASE}/tables`);
  const json = await res.json();
  return json.data || [];
}

async function fetchRounds(tableId: string): Promise<RoundOption[]> {
  const res = await fetch(`${API_BASE}/rounds/${tableId}`);
  const json = await res.json();
  return json.data || [];
}

async function fetchSeats(tableId: string, handNumber: number): Promise<SeatOption[]> {
  const res = await fetch(`${API_BASE}/seats/${tableId}/${handNumber}`);
  const json = await res.json();
  return json.data || [];
}

async function fetchReplay(tableId: string, handNumber: number, seat?: number): Promise<{ events: ReplayEvent[]; totalEvents: number }> {
  const url = seat
    ? `${API_BASE}/replay/${tableId}/${handNumber}?seat=${seat}`
    : `${API_BASE}/replay/${tableId}/${handNumber}`;
  const res = await fetch(url);
  const json = await res.json();
  return json.data || { events: [], totalEvents: 0 };
}

async function fetchSummary(tableId: string, handNumber: number): Promise<ReplaySummary | null> {
  const res = await fetch(`${API_BASE}/summary/${tableId}/${handNumber}`);
  const json = await res.json();
  return json.data || null;
}

// ===========================================
// CARD COMPONENT
// ===========================================

function Card({ rank, suit, faceDown, small }: CardDisplay & { small?: boolean }) {
  const suitSymbols: Record<string, string> = {
    hearts: '♥',
    diamonds: '♦',
    clubs: '♣',
    spades: '♠',
  };
  const suitColors: Record<string, string> = {
    hearts: 'text-red-500',
    diamonds: 'text-red-500',
    clubs: 'text-gray-900',
    spades: 'text-gray-900',
  };

  if (faceDown) {
    return (
      <div className={`${small ? 'w-8 h-12' : 'w-12 h-16'} bg-blue-800 rounded border-2 border-blue-600 flex items-center justify-center`}>
        <span className="text-blue-400 text-lg">?</span>
      </div>
    );
  }

  return (
    <div className={`${small ? 'w-8 h-12 text-xs' : 'w-12 h-16 text-sm'} bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow`}>
      <span className={`font-bold ${suitColors[suit] || 'text-gray-900'}`}>{rank}</span>
      <span className={suitColors[suit] || 'text-gray-900'}>{suitSymbols[suit] || suit}</span>
    </div>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function DemoEventsPage() {
  // Selection state
  const [tables, setTables] = useState<TableOption[]>([]);
  const [rounds, setRounds] = useState<RoundOption[]>([]);
  const [seats, setSeats] = useState<SeatOption[]>([]);

  const [selectedTable, setSelectedTable] = useState<string>('');
  const [selectedRound, setSelectedRound] = useState<number | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);

  // Replay state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [events, setEvents] = useState<ReplayEvent[]>([]);
  const [summary, setSummary] = useState<ReplaySummary | null>(null);

  // Visual state (cards on table)
  const [dealerCards, setDealerCards] = useState<CardDisplay[]>([]);
  const [playerCards, setPlayerCards] = useState<Map<number, CardDisplay[]>>(new Map());
  const [dealerScore, setDealerScore] = useState<{ score: number; isSoft: boolean } | null>(null);
  const [playerScores, setPlayerScores] = useState<Map<number, { score: number; isSoft: boolean }>>(new Map());
  const [outcomes, setOutcomes] = useState<Map<number, string>>(new Map());
  const [currentPhase, setCurrentPhase] = useState<string>('waiting');

  // Event log scroll ref
  const logRef = useRef<HTMLDivElement>(null);

  // Load tables on mount
  useEffect(() => {
    fetchTables().then(setTables);
  }, []);

  // Load rounds when table selected
  useEffect(() => {
    if (selectedTable) {
      fetchRounds(selectedTable).then(setRounds);
      setSelectedRound(null);
      setSelectedSeat(null);
      setSeats([]);
    }
  }, [selectedTable]);

  // Load seats when round selected
  useEffect(() => {
    if (selectedTable && selectedRound !== null) {
      fetchSeats(selectedTable, selectedRound).then(setSeats);
      fetchSummary(selectedTable, selectedRound).then(setSummary);
      setSelectedSeat(null);
    }
  }, [selectedTable, selectedRound]);

  // Reset visual state
  const resetVisuals = useCallback(() => {
    setDealerCards([]);
    setPlayerCards(new Map());
    setDealerScore(null);
    setPlayerScores(new Map());
    setOutcomes(new Map());
    setCurrentPhase('waiting');
  }, []);

  // GO! - Start replay
  const handleGo = useCallback(async () => {
    if (!selectedTable || selectedRound === null) return;

    // Clear previous state
    resetVisuals();
    setCurrentEventIndex(0);
    setIsPlaying(true);
    setIsPaused(false);

    // Fetch events
    const data = await fetchReplay(selectedTable, selectedRound, selectedSeat || undefined);
    setEvents(data.events);

    console.log(`[DemoEvents] Loaded ${data.events.length} events for replay`);
  }, [selectedTable, selectedRound, selectedSeat, resetVisuals]);

  // Process a single event
  const processEvent = useCallback((event: ReplayEvent) => {
    console.log(`[DemoEvents] Processing: ${event.type}`, event);

    switch (event.type) {
      case 'ROUND_STARTED':
        setCurrentPhase('betting');
        break;

      case 'DEALING_STARTED':
        setCurrentPhase('dealing');
        break;

      case 'CARD_DEALT':
        if (event.target === 'dealer') {
          setDealerCards(prev => [...prev, {
            rank: event.card?.rank || '?',
            suit: event.card?.suit || 'spades',
            faceDown: event.cardIndex === 1, // Hole card face down
          }]);
        } else if (event.seatPosition) {
          setPlayerCards(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(event.seatPosition!) || [];
            newMap.set(event.seatPosition!, [...existing, {
              rank: event.card?.rank || '?',
              suit: event.card?.suit || 'spades',
            }]);
            return newMap;
          });
        }
        break;

      case 'SCORE_UPDATED':
        if (event.target === 'dealer') {
          setDealerScore({ score: event.score, isSoft: event.isSoft });
        } else if (event.seatPosition) {
          setPlayerScores(prev => {
            const newMap = new Map(prev);
            newMap.set(event.seatPosition!, { score: event.score, isSoft: event.isSoft });
            return newMap;
          });
        }
        break;

      case 'PLAYER_TURNS_STARTED':
        setCurrentPhase('player_turns');
        break;

      case 'DEALER_TURN_STARTED':
        setCurrentPhase('dealer_turn');
        break;

      case 'DEALER_REVEALS_HOLE':
        setDealerCards(prev => prev.map((card, i) =>
          i === 1 ? { rank: event.holeCard?.rank || '?', suit: event.holeCard?.suit || 'spades', faceDown: false } : card
        ));
        setDealerScore({ score: event.score, isSoft: event.isSoft });
        break;

      case 'DEALER_HITS':
        setDealerCards(prev => [...prev, {
          rank: event.card?.rank || '?',
          suit: event.card?.suit || 'spades',
        }]);
        setDealerScore({ score: event.newScore, isSoft: event.isSoft });
        break;

      case 'HAND_RESOLVED':
        setOutcomes(prev => {
          const newMap = new Map(prev);
          newMap.set(event.seatPosition, event.outcome);
          return newMap;
        });
        break;

      case 'HAND_BUSTED':
        setOutcomes(prev => {
          const newMap = new Map(prev);
          newMap.set(event.seatPosition, 'bust');
          return newMap;
        });
        break;

      case 'HAND_BLACKJACK':
        setOutcomes(prev => {
          const newMap = new Map(prev);
          newMap.set(event.seatPosition, 'blackjack');
          return newMap;
        });
        break;

      case 'ROUND_COMPLETED':
        setCurrentPhase('complete');
        break;

      default:
        // Other events - just log
        break;
    }
  }, []);

  // Event playback loop
  useEffect(() => {
    if (!isPlaying || isPaused || events.length === 0) return;
    if (currentEventIndex >= events.length) {
      setIsPlaying(false);
      console.log('[DemoEvents] Replay complete');
      return;
    }

    const event = events[currentEventIndex];
    const delay = Math.max(50, event.delay / playbackSpeed);

    const timer = setTimeout(() => {
      processEvent(event);
      setCurrentEventIndex((i) => i + 1);

      // Auto-scroll event log
      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [isPlaying, isPaused, currentEventIndex, events, playbackSpeed, processEvent]);

  // Pause/Resume
  const togglePause = () => setIsPaused(!isPaused);

  // Reset
  const handleReset = () => {
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentEventIndex(0);
    resetVisuals();
  };

  // Step forward
  const handleStep = () => {
    if (currentEventIndex < events.length) {
      processEvent(events[currentEventIndex]);
      setCurrentEventIndex((i) => i + 1);
    }
  };

  // Get event color
  const getEventColor = (type: string) => {
    if (type.includes('CARD')) return 'text-blue-400';
    if (type.includes('SCORE')) return 'text-cyan-400';
    if (type.includes('BET') || type.includes('CHIP')) return 'text-yellow-400';
    if (type.includes('RESOLVED') || type.includes('COMPLETE')) return 'text-green-400';
    if (type.includes('BUST')) return 'text-red-400';
    if (type.includes('BLACKJACK')) return 'text-purple-400';
    if (type.includes('DEALER')) return 'text-orange-400';
    return 'text-gray-400';
  };

  // Get outcome color
  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'win': return 'text-green-400 bg-green-900/50';
      case 'blackjack': return 'text-purple-400 bg-purple-900/50';
      case 'push': return 'text-yellow-400 bg-yellow-900/50';
      case 'lose': return 'text-red-400 bg-red-900/50';
      case 'bust': return 'text-red-500 bg-red-900/50';
      default: return 'text-gray-400 bg-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <h1 className="text-2xl font-bold text-amber-400 mb-4">Event Sourcing Replay</h1>

        {/* Selection Controls */}
        <div className="flex flex-wrap gap-4 items-end">
          {/* Table Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-1">Table</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white min-w-[200px]"
            >
              <option value="">Select table...</option>
              {tables.map((t) => (
                <option key={t.tableId} value={t.tableId}>
                  {t.tableId} ({t.roundCount} rounds)
                </option>
              ))}
            </select>
          </div>

          {/* Round Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-1">Round</label>
            <select
              value={selectedRound ?? ''}
              onChange={(e) => setSelectedRound(e.target.value ? parseInt(e.target.value) : null)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white min-w-[180px]"
              disabled={!selectedTable}
            >
              <option value="">Select round...</option>
              {rounds.map((r) => (
                <option key={r.handNumber} value={r.handNumber}>
                  Hand #{r.handNumber} ({r.eventCount} events) {r.isComplete ? '✓' : '...'}
                </option>
              ))}
            </select>
          </div>

          {/* Seat Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-1">Seat (optional)</label>
            <select
              value={selectedSeat ?? ''}
              onChange={(e) => setSelectedSeat(e.target.value ? parseInt(e.target.value) : null)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white min-w-[140px]"
              disabled={!selectedRound}
            >
              <option value="">All seats</option>
              {seats.map((s) => (
                <option key={s.seatPosition} value={s.seatPosition}>
                  Seat {s.seatPosition}
                </option>
              ))}
            </select>
          </div>

          {/* GO! Button */}
          <button
            onClick={handleGo}
            disabled={!selectedTable || selectedRound === null}
            className="bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-2 rounded font-bold text-lg transition-colors"
          >
            GO!
          </button>
        </div>
      </div>

      {/* Playback Controls */}
      {events.length > 0 && (
        <div className="bg-gray-800 border-b border-gray-700 p-3 flex items-center gap-4 flex-wrap">
          <button
            onClick={togglePause}
            className={`px-4 py-1 rounded font-semibold ${isPaused ? 'bg-green-600' : 'bg-yellow-600'}`}
          >
            {isPaused ? '▶ Resume' : '⏸ Pause'}
          </button>

          <button
            onClick={handleStep}
            disabled={isPlaying && !isPaused}
            className="px-4 py-1 rounded font-semibold bg-blue-600 disabled:bg-gray-600"
          >
            Step →
          </button>

          <button
            onClick={handleReset}
            className="px-4 py-1 rounded font-semibold bg-red-600"
          >
            Reset
          </button>

          <div className="flex items-center gap-2">
            <span className="text-gray-400">Speed:</span>
            {[0.5, 1, 2, 4, 10].map((speed) => (
              <button
                key={speed}
                onClick={() => setPlaybackSpeed(speed)}
                className={`px-2 py-1 rounded text-sm ${playbackSpeed === speed ? 'bg-amber-600' : 'bg-gray-600'}`}
              >
                {speed}x
              </button>
            ))}
          </div>

          <div className="ml-auto text-gray-400">
            Event {currentEventIndex} / {events.length}
            {isPlaying && !isPaused && <span className="ml-2 text-green-400">▶ Playing</span>}
            {isPaused && <span className="ml-2 text-yellow-400">⏸ Paused</span>}
            {!isPlaying && currentEventIndex > 0 && <span className="ml-2 text-blue-400">✓ Complete</span>}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Visual Display */}
        <div className="flex-1 p-6 flex flex-col items-center">
          {/* Phase indicator */}
          <div className="mb-4 px-4 py-2 bg-gray-800 rounded-full text-sm uppercase tracking-wider">
            Phase: <span className="text-amber-400 font-semibold">{currentPhase}</span>
          </div>

          {/* Dealer Area */}
          <div className="mb-8 text-center">
            <div className="text-gray-400 text-sm mb-2">DEALER</div>
            <div className="flex gap-2 justify-center mb-2">
              {dealerCards.map((card, i) => (
                <Card key={i} {...card} />
              ))}
              {dealerCards.length === 0 && (
                <div className="w-12 h-16 border-2 border-dashed border-gray-600 rounded flex items-center justify-center text-gray-600">
                  -
                </div>
              )}
            </div>
            {dealerScore && (
              <div className="text-xl font-bold">
                {dealerScore.isSoft && dealerScore.score <= 21 ? `${dealerScore.score - 10}/${dealerScore.score}` : dealerScore.score}
              </div>
            )}
          </div>

          {/* Player Areas */}
          <div className="flex gap-8 flex-wrap justify-center">
            {Array.from(playerCards.entries()).map(([seatPos, cards]) => (
              <div key={seatPos} className="text-center p-4 bg-gray-800 rounded-lg min-w-[150px]">
                <div className="text-gray-400 text-sm mb-2">SEAT {seatPos}</div>
                <div className="flex gap-1 justify-center mb-2">
                  {cards.map((card, i) => (
                    <Card key={i} {...card} small />
                  ))}
                </div>
                {playerScores.get(seatPos) && (
                  <div className="text-lg font-bold mb-2">
                    {playerScores.get(seatPos)!.isSoft && playerScores.get(seatPos)!.score <= 21
                      ? `${playerScores.get(seatPos)!.score - 10}/${playerScores.get(seatPos)!.score}`
                      : playerScores.get(seatPos)!.score}
                  </div>
                )}
                {outcomes.get(seatPos) && (
                  <div className={`px-3 py-1 rounded font-bold uppercase text-sm ${getOutcomeColor(outcomes.get(seatPos)!)}`}>
                    {outcomes.get(seatPos)}
                  </div>
                )}
              </div>
            ))}
            {playerCards.size === 0 && (
              <div className="text-gray-500 italic">No player cards yet</div>
            )}
          </div>

          {/* Summary */}
          {summary && currentPhase === 'complete' && (
            <div className="mt-8 p-4 bg-gray-800 rounded-lg">
              <div className="font-semibold text-amber-400 mb-2">Round Complete</div>
              <div className="text-2xl font-bold">
                Net: <span className={summary.totalNet >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {summary.totalNet >= 0 ? '+' : ''}{summary.totalNet.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Event Log */}
        <div className="w-96 bg-gray-800 border-l border-gray-700 flex flex-col">
          <div className="p-3 border-b border-gray-700 font-semibold text-amber-400">
            Event Log
          </div>
          <div ref={logRef} className="flex-1 overflow-y-auto p-3 font-mono text-xs space-y-1">
            {events.slice(0, currentEventIndex).map((event, i) => (
              <div key={i} className={`${getEventColor(event.type)} ${i === currentEventIndex - 1 ? 'bg-gray-700 -mx-3 px-3 py-1' : ''}`}>
                <span className="text-gray-500">{String(event.sequenceNumber).padStart(2, '0')}</span>{' '}
                <span className="font-semibold">{event.type}</span>
                {event.seatPosition && <span className="text-gray-500"> seat={event.seatPosition}</span>}
                {event.target && <span className="text-gray-500"> target={event.target}</span>}
                {event.card && <span className="text-white"> {event.card.rank}{event.card.suit[0]}</span>}
                {event.score !== undefined && <span className="text-cyan-300"> score={event.score}</span>}
                {event.outcome && <span className="text-yellow-300"> {event.outcome}</span>}
                {event.netResult !== undefined && (
                  <span className={event.netResult >= 0 ? 'text-green-300' : 'text-red-300'}>
                    {' '}{event.netResult >= 0 ? '+' : ''}{event.netResult}
                  </span>
                )}
              </div>
            ))}
            {events.length === 0 && (
              <div className="text-gray-500 italic">Select a round and click GO!</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
